import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Globe, Send } from "lucide-react";
import { toast } from "sonner";

const OUTCOME_STYLE = {
  dispatched: "bg-emerald-100 text-emerald-700", skipped_not_published: "bg-slate-100 text-slate-600",
  skipped_merch: "bg-slate-100 text-slate-600", skipped_disabled: "bg-amber-100 text-amber-700",
  skipped_missing_config: "bg-amber-100 text-amber-700", product_not_found: "bg-red-100 text-red-700",
};

export default function WebsiteSync() {
  const [settings, setSettings] = useState({ url: "", secret: "", enabled: false });
  const [data, setData] = useState({ traces: [], counts: {} });

  const load = useCallback(() => {
    api.get("/sync/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/sync/traces").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(() => api.get("/sync/traces").then((r) => setData(r.data)).catch(() => {}), 10000); return () => clearInterval(t); }, [load]);

  const saveSettings = () => api.put("/sync/settings", settings).then(() => toast.success("Sync settings saved")).catch((e) => toast.error(formatApiError(e)));
  const test = () => api.post("/sync/test").then(() => { toast.success("Test ping dispatched"); setTimeout(load, 1500); }).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="max-w-5xl space-y-5">
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-slate-800"><Globe className="w-5 h-5 text-blue-600" />Website Sync Settings</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Webhook URL</Label><Input value={settings.url} onChange={(e) => setSettings({ ...settings, url: e.target.value })} placeholder="https://yoursite.com/wp-json/pos/sync" data-testid="sync-url-input" /></div>
          <div><Label className="text-xs">HMAC secret</Label><Input value={settings.secret} onChange={(e) => setSettings({ ...settings, secret: e.target.value })} data-testid="sync-secret-input" /></div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm"><Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="sync-enabled-switch" /> Sync enabled</label>
          <div className="flex gap-2"><Button variant="outline" onClick={test} data-testid="sync-test-btn"><Send className="w-4 h-4 mr-1.5" />Send test</Button><Button onClick={saveSettings} data-testid="sync-save-btn">Save</Button></div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="font-semibold text-slate-800 mb-3">Dispatch Call Trace</div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {["dispatched", "skipped_not_published", "skipped_merch", "skipped_disabled", "skipped_missing_config", "product_not_found"].map((k) => (
            <div key={k} className="rounded-lg bg-slate-50 p-2.5 text-center" data-testid={`trace-count-${k}`}>
              <div className="text-lg font-bold text-slate-800">{data.counts[k] || 0}</div>
              <div className="text-[10px] text-slate-500">{k.replace(/_/g, " ")}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="divide-y">
        {data.traces.map((t) => (
          <div key={t.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`trace-row-${t.id}`}>
            <Badge className={OUTCOME_STYLE[t.outcome] || "bg-slate-100"}>{t.outcome}</Badge>
            <div className="flex-1"><span className="font-medium">{t.product_name}</span> · {t.event}<div className="text-xs text-slate-400">from {t.called_from}{t.http_status != null ? ` · HTTP ${t.http_status}` : ""}</div></div>
            <span className="text-xs text-slate-400">{fmtDate(t.at)}</span>
          </div>
        ))}
        {data.traces.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No sync activity yet.</div>}
      </Card>
    </div>
  );
}
